// The IDE's side of its server (web/server): accounts signed in with
// passkeys, projects kept in an account, share links, and shader
// translation. A site served without the server (any static host) has no
// api/config, and the IDE then works as before, with projects kept in the
// browser only.

const b64 = {
  encode(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let k = 0; k < bytes.length; k += 0x8000) binary += String.fromCharCode(...bytes.subarray(k, k + 0x8000));
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  },
  decode(text) {
    const binary = atob(text.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binary.length);
    for (let k = 0; k < binary.length; ++k) bytes[k] = binary.charCodeAt(k);
    return bytes.buffer;
  },
};

// Plain base64, for file contents in JSON.
export const base64 = {
  encode: (bytes) => b64.encode(bytes).replace(/-/g, "+").replace(/_/g, "/"),
  decode: (text) => new Uint8Array(b64.decode(text.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""))),
};

export class CloudError extends Error {
  constructor(status, message, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Each file's SHA-256, in hex, kept while the Blob is the same.
const hashes = new WeakMap();
export async function hashBlob(blob) {
  if (hashes.has(blob)) return hashes.get(blob);
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  const hex = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  hashes.set(blob, hex);
  return hex;
}

export class Cloud {
  // The server beside the IDE's page, or null when there is none.
  static async connect(base = new URL("api/", location.href)) {
    try {
      const response = await fetch(new URL("config", base), { credentials: "same-origin" });
      if (!response.ok || !(response.headers.get("content-type") || "").includes("json")) return null;
      const cloud = new Cloud(base, await response.json());
      cloud.user = (await cloud.request("GET", "me")).user;
      return cloud;
    } catch {
      return null;
    }
  }

  constructor(base, config) {
    this.base = base;
    this.config = config;
    this.user = null;
  }

  get canUsePasskeys() { return typeof PublicKeyCredential === "function" && !!navigator.credentials; }
  get translatesShaders() { return !!this.config.shaders; }

  async request(method, path, { json, body, raw } = {}) {
    const headers = {};
    if (method !== "GET") headers["X-B3D-IDE"] = "1";
    if (json !== undefined) headers["Content-Type"] = "application/json";
    const response = await fetch(new URL(path, this.base), { method, headers, credentials: "same-origin",
      body: json !== undefined ? JSON.stringify(json) : body });
    if (raw && response.ok) return response;
    const result = (response.headers.get("content-type") || "").includes("json") ? await response.json() : {};
    if (!response.ok) throw new CloudError(response.status, result.error || `The server answered ${response.status}`, result);
    return result;
  }

  // Passkeys.
  async createPasskey(path, json) {
    const options = await this.request("POST", `${path}/options`, { json });
    const publicKey = {
      ...options,
      challenge: b64.decode(options.challenge),
      user: { ...options.user, id: b64.decode(options.user.id) },
      excludeCredentials: options.excludeCredentials.map((item) => ({ ...item, id: b64.decode(item.id) })),
    };
    const credential = await navigator.credentials.create({ publicKey });
    const response = credential.response;
    return this.request("POST", `${path}/verify`, { json: { credential: {
      id: credential.id, type: credential.type,
      response: {
        clientDataJSON: b64.encode(response.clientDataJSON),
        attestationObject: b64.encode(response.attestationObject),
        transports: response.getTransports ? response.getTransports() : [],
      },
    } } });
  }

  // join is a join code, which closed accounts need (server.mjs --registration).
  async register(name, join = null) {
    this.user = (await this.createPasskey("auth/register", { name, ...(join ? { join } : {}) })).user;
    return this.user;
  }

  async addPasskey() {
    this.user = (await this.createPasskey("auth/passkeys", {})).user;
    return this.user;
  }

  async signIn() {
    const options = await this.request("POST", "auth/login/options", { json: {} });
    const credential = await navigator.credentials.get({ publicKey: { ...options,
      challenge: b64.decode(options.challenge), allowCredentials: [] } });
    const response = credential.response;
    this.user = (await this.request("POST", "auth/login/verify", { json: { credential: {
      id: credential.id, type: credential.type,
      response: {
        clientDataJSON: b64.encode(response.clientDataJSON),
        authenticatorData: b64.encode(response.authenticatorData),
        signature: b64.encode(response.signature),
        userHandle: response.userHandle ? b64.encode(response.userHandle) : null,
      },
    } } })).user;
    return this.user;
  }

  async signOut() {
    await this.request("POST", "auth/logout", { json: {} });
    this.user = null;
  }

  // Projects.
  listProjects() { return this.request("GET", "projects").then((result) => result.projects); }
  createProject(meta) { return this.request("POST", "projects", { json: meta }); }
  getProject(id) { return this.request("GET", `projects/${id}`); }
  deleteProject(id) { return this.request("DELETE", `projects/${id}`); }

  // Saves files ([[path, Blob], ...]) as the project's next revision, after
  // uploading the ones the server lacks. Throws a CloudError with status 409
  // when the project changed elsewhere since baseRevision.
  async saveProject(id, { name, main, mode, files, baseRevision }) {
    const manifest = {};
    for (const [path, blob] of files) manifest[path] = await hashBlob(blob);
    const byHash = new Map(files.map(([path, blob]) => [manifest[path], blob]));
    const { missing } = await this.request("POST", "blobs/missing", { json: { hashes: [...byHash.keys()] } });
    for (const hash of missing) await this.request("PUT", `blobs/${hash}`, { body: byHash.get(hash) });
    const saved = await this.request("PUT", `projects/${id}`, { json: { name, main, mode, files: manifest, baseRevision } });
    return { ...saved, manifest };
  }

  async projectFile(id, hash) {
    return (await this.request("GET", `projects/${id}/blobs/${hash}`, { raw: true })).blob();
  }

  // Share links.
  share(id) { return this.request("POST", `projects/${id}/shares`, { json: {} }).then((result) => result.token); }
  getShare(token) { return this.request("GET", `shares/${token}`); }
  async sharedFile(token, hash) {
    return (await this.request("GET", `shares/${token}/blobs/${hash}`, { raw: true })).blob();
  }

  // Translates a shader asset: files { path: Uint8Array }. Returns { ok,
  // output, files: { path: Uint8Array } }.
  async translateShader(asset, files) {
    const encoded = {};
    for (const [path, bytes] of Object.entries(files)) encoded[path] = base64.encode(bytes);
    const result = await this.request("POST", "shaders/translate", { json: { asset, files: encoded } });
    const decoded = {};
    for (const [path, text] of Object.entries(result.files || {})) decoded[path] = base64.decode(text);
    return { ok: result.ok, output: result.output, files: decoded };
  }
}
